import { Plus, Trash } from '@phosphor-icons/react'
import { TimePicker, Input } from 'antd'
import SelectDateFromGivenDates from 'components/global/SelectDateFromGivenDates'
import React, { useState } from 'react'
import { creationEventStore } from 'state/event/eventCreationStore'
import moment from 'moment'
import {
  convertMinutesToLocalTime,
  convertTimeToMinutes,
  convertTimeToUtcMinutes,
} from 'helper/utility'
import { eventCommonStore } from 'state/event/eventCommon'
import { DateShift, Shift } from 'types/mentorRequest/mentorRequest'

interface AddShiftsProps {
  mentorShifts: DateShift[]
  setMentorShifts: React.Dispatch<React.SetStateAction<DateShift[]>>
}

const AddShifts: React.FC<AddShiftsProps> = ({
  mentorShifts,
  setMentorShifts,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(
    new Date(new Date().setHours(0, 0, 0, 0)),
  )
  const { eventData } = creationEventStore(store => ({
    eventData: store.eventData,
  }))

  const handleAddShift = () => {
    const defaultStartTime = eventData.timings?.startTime || 0
    const defaultEndTime = eventData.timings?.endTime || 0
    const dateStr = moment(selectedDate).toISOString()

    const newShift: Shift = {
      start: defaultStartTime,
      end: defaultEndTime,
      mentorsRequired: 0,
    }

    setMentorShifts(prevShifts => {
      const existingDateIndex = prevShifts.findIndex(ds => ds.date === dateStr)

      if (existingDateIndex >= 0) {
        // Add shift to existing date
        const updatedShifts = [...prevShifts]
        updatedShifts[existingDateIndex].shifts.push(newShift)
        return updatedShifts
      } else {
        // Create new date entry
        return [...prevShifts, { date: dateStr, shifts: [newShift] }]
      }
    })
  }

  const handleDeleteShift = (dateStr: string, shiftIndex: number) => {
    setMentorShifts(prevShifts => {
      const dateIndex = prevShifts.findIndex(ds => ds.date === dateStr)
      if (dateIndex >= 0) {
        const updatedShifts = [...prevShifts]
        updatedShifts[dateIndex].shifts = updatedShifts[
          dateIndex
        ].shifts.filter((_, index) => index !== shiftIndex)

        // Remove date entry if no shifts left
        if (updatedShifts[dateIndex].shifts.length === 0) {
          return updatedShifts.filter((_, index) => index !== dateIndex)
        }
        return updatedShifts
      }
      return prevShifts
    })
  }

  const handleTimeChange = (
    dateStr: string,
    shiftIndex: number,
    type: 'start' | 'end',
    value: moment.Moment | null,
  ) => {
    setMentorShifts(prevShifts => {
      const dateIndex = prevShifts.findIndex(ds => ds.date === dateStr)
      if (dateIndex >= 0) {
        const updatedShifts = [...prevShifts]
        const minutes = value
          ? convertTimeToUtcMinutes(value.format('HH:mm'))
          : 0
        updatedShifts[dateIndex].shifts[shiftIndex] = {
          ...updatedShifts[dateIndex].shifts[shiftIndex],
          [type]: minutes,
        }
        return updatedShifts
      }
      return prevShifts
    })
  }

  const handleMentorsChange = (
    dateStr: string,
    shiftIndex: number,
    value: number,
  ) => {
    setMentorShifts(prevShifts => {
      const dateIndex = prevShifts.findIndex(ds => ds.date === dateStr)
      if (dateIndex >= 0) {
        const updatedShifts = [...prevShifts]
        updatedShifts[dateIndex].shifts[shiftIndex] = {
          ...updatedShifts[dateIndex].shifts[shiftIndex],
          mentorsRequired: value,
        }
        return updatedShifts
      }
      return prevShifts
    })
  }

  const getShiftsForSelectedDate = () => {
    const dateStr = moment(selectedDate).toISOString()
    const dateShift = mentorShifts.find(ds => ds.date === dateStr)
    return dateShift?.shifts || []
  }

  return (
    <>
      <div>
        <div className="mx-auto" style={{ width: '360px' }}>
          <SelectDateFromGivenDates
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            availableDates={eventData.datesOfEvent}
          />
        </div>
        <div className="mt-20px">
          <div className="flex justify-between align-center">
            <h3 className="text-14 w-600">Shifts</h3>
            <Plus
              size={20}
              color="#000"
              className="pointer"
              onClick={handleAddShift}
            />
          </div>
        </div>

        {getShiftsForSelectedDate().map((shift, index) => (
          <div key={index} className="mt-20px mb-20px">
            <div className="flex justify-between align-center">
              <div className="flex gap-10px justify-between align-center">
                <div className="flex flex-col gap-10px">
                  <p className="m-0">Start time</p>
                  <TimePicker
                    format="h:mm a"
                    placeholder="Select time"
                    className="w-150px h-50px"
                    showNow={false}
                    clearIcon={false}
                    use12Hours={true}
                    value={
                      shift.start
                        ? moment(
                            convertMinutesToLocalTime(shift.start),
                            'hh:mm A',
                          )
                        : null
                    }
                    onChange={time =>
                      handleTimeChange(
                        moment(selectedDate).toISOString(),
                        index,
                        'start',
                        time,
                      )
                    }
                  />
                </div>
                <p className="mt-30px">-</p>
                <div className="flex flex-col gap-10px">
                  <p className="m-0">End time</p>
                  <TimePicker
                    className="w-150px h-50px form-control-field"
                    format="h:mm a"
                    placeholder="Select time"
                    showNow={false}
                    clearIcon={false}
                    use12Hours={true}
                    value={
                      shift.end
                        ? moment(
                            convertMinutesToLocalTime(shift.end),
                            'hh:mm A',
                          )
                        : null
                    }
                    onChange={time =>
                      handleTimeChange(
                        moment(selectedDate).toISOString(),
                        index,
                        'end',
                        time,
                      )
                    }
                  />
                </div>
              </div>
              <div className="mt-40px">
                <Trash
                  size={20}
                  color="#FF6961"
                  className="pointer"
                  onClick={() =>
                    handleDeleteShift(moment(selectedDate).toISOString(), index)
                  }
                />
              </div>
            </div>
            <div className="mt-20px">
              <h3 className="text-16 w-600">Shift requirements (optional)</h3>
              <div className="flex align-center justify-between gap-10px">
                <p className="text-14 w-500">Required Mentors</p>
                <div className="w-150px">
                  <Input
                    name="numberOfMentors"
                    placeholder="0"
                    className="h-50px"
                    type="number"
                    value={shift.mentorsRequired}
                    onChange={e =>
                      handleMentorsChange(
                        moment(selectedDate).toISOString(),
                        index,
                        parseInt(e.target.value) || 0,
                      )
                    }
                    onWheel={e => e.currentTarget.blur()}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export default AddShifts
